import { SerializedData } from 'bernie-core';

import { FlightsSearchFormJourneyCriteriaType } from 'types/search/wizard';
import { ILocalization } from '@shared-ui/localization-context';
import { FlightsResponsiveMessage } from '__generated__/api';
import FlightsMessage = FlightsResponsiveMessage.Fragment;

// business logic in the event that bex-api does not return this
// TODO: remove when scenario no longer occurs

const getLoadingSearchFormTriggerLocationLabel = (firstJourney: FlightsSearchFormJourneyCriteriaType) => {
  const { destinationLocation, originLocation } = firstJourney;

  return `${originLocation.value} - ${destinationLocation.value}`;
};

const getLoadedSearchFormTriggerLocationLabel = (
  originAndDestinationLocations: string,
  firstJourney: FlightsSearchFormJourneyCriteriaType
) => {
  if (originAndDestinationLocations) {
    return originAndDestinationLocations;
  }

  const originLocationValue = firstJourney.originLocation.value;
  const destinationLocationValue = firstJourney.destinationLocation.value;

  return `${originLocationValue} - ${destinationLocationValue}`;
};

const getLoadingSearchFormTriggerDateLabel = (
  firstJourney: FlightsSearchFormJourneyCriteriaType,
  l10n: ILocalization
) => {
  return {
    shortMessage: l10n.formatDateString(firstJourney.departureDate.date?.isoFormat, { skeleton: 'MMMd' }),
    longMessage: l10n.formatDateString(firstJourney.departureDate.date?.isoFormat, { skeleton: 'MMMEd' })
  };
};

const getLoadedSearchFormTriggerDateLabel = (
  departureAndReturnDates: FlightsMessage,
  firstJourney: FlightsSearchFormJourneyCriteriaType
) => {
  if (departureAndReturnDates) {
    return {
      shortMessage: departureAndReturnDates.shortMessage,
      longMessage: departureAndReturnDates.longMessage
    };
  }

  return {
    shortMessage: firstJourney.departureDate.date?.isoFormat,
    longMessage: firstJourney.departureDate.date?.isoFormat
  };
};

const shouldDisplayLocationLoadingState = (
  firstJourney: FlightsSearchFormJourneyCriteriaType,
  searchFormInitialState: SerializedData,
  searchFormLoading: boolean
): boolean => {
  const { originLocation, destinationLocation } = firstJourney;
  const previousSearchForm = searchFormInitialState && searchFormInitialState.searchForm;

  if (!previousSearchForm) {
    return false;
  }

  const previousFirstJourney = previousSearchForm.journeys[0];

  return (
    searchFormLoading &&
    previousSearchForm &&
    (originLocation.value !== previousFirstJourney.originLocation.value ||
      destinationLocation.value !== previousFirstJourney.destinationLocation.value)
  );
};

const shouldDisplayDateLoadingState = (
  firstJourney: FlightsSearchFormJourneyCriteriaType,
  searchFormInitialState: SerializedData,
  searchFormLoading: boolean
): boolean => {
  const { departureDate } = firstJourney;
  const previousSearchForm = searchFormInitialState && searchFormInitialState.searchForm;

  if (!previousSearchForm) {
    return false;
  }

  const previousFirstJourney = previousSearchForm.journeys[0];

  return (
    searchFormLoading &&
    previousSearchForm &&
    departureDate.date.isoFormat !== previousFirstJourney.departureDate.date.isoFormat
  );
};

export const getSearchFormTriggerLocationLabel = (
  originAndDestinationLocations: string,
  firstJourney: FlightsSearchFormJourneyCriteriaType,
  searchFormInitialState: SerializedData,
  searchFormLoading: boolean
) => {
  const loading = shouldDisplayLocationLoadingState(firstJourney, searchFormInitialState, searchFormLoading);

  if (loading) {
    return getLoadingSearchFormTriggerLocationLabel(firstJourney);
  }

  return getLoadedSearchFormTriggerLocationLabel(originAndDestinationLocations, firstJourney);
};

export const getSearchFormTriggerDateLabel = (
  departureAndReturnDates: FlightsMessage,
  firstJourney: FlightsSearchFormJourneyCriteriaType,
  searchFormInitialState: SerializedData,
  searchFormLoading: boolean,
  l10n: ILocalization
) => {
  const loading = shouldDisplayDateLoadingState(firstJourney, searchFormInitialState, searchFormLoading);
  if (loading) {
    return getLoadingSearchFormTriggerDateLabel(firstJourney, l10n);
  }

  return getLoadedSearchFormTriggerDateLabel(departureAndReturnDates, firstJourney);
};
